import { TFunction } from "i18next";

import { TCarbonUnit } from "@/types";

type TRange = { min: number; max: number };

const CARBON_UNIT_RANGES = {
  [TCarbonUnit.tCO2e]: { min: 1, max: Number.POSITIVE_INFINITY },
  [TCarbonUnit.kgCO2e]: { min: 0.001, max: 1 },
  [TCarbonUnit.gCO2e]: { min: 0, max: 0.001 },
} as Record<TCarbonUnit, TRange>;

// Checks if x is in the range [min, max[.
export function isInRange(x: number, range: TRange): boolean {
  return x >= range.min && x < range.max;
}

// Returns the unit that best fits the values in the list.
// That is, the largest unit that fits at least one value.
export function getCarbonDisplayUnit(values: Array<number>): TCarbonUnit {
  for (const [rangeName, range] of Object.entries(CARBON_UNIT_RANGES)) {
    if (values.some((value) => isInRange(value, range))) {
      return rangeName as TCarbonUnit;
    }
  }
  return TCarbonUnit.tCO2e;
}

// Converts tons to kilos.
function convertTonToKilo(value: number) {
  return value * 1000;
}

// Converts tons to grams.
function convertTonToGram(value: number) {
  return value * 1000000;
}

// Converts carbon to the unit provided (or whichever is more suitable).
// Then format the value to be displayed.
// It returns the unit, the formatted carbon (the localized string) and the converted carbon (a number).
export function formatCarbon(
  t: TFunction,
  carbon: number,
  forcedUnit?: TCarbonUnit,
  options?: { shouldUnescape: boolean }
): {
  formattedCarbon: string;
  unit: TCarbonUnit;
  convertedCarbon: number;
} {
  let unit = forcedUnit || TCarbonUnit.tCO2e;
  let convertedCarbon =
    Number.isNaN(carbon) || !Number.isFinite(carbon) ? 0 : carbon;

  if (convertedCarbon !== 0) {
    // Convert to the unit provided
    switch (forcedUnit) {
      case TCarbonUnit.kgCO2e:
        convertedCarbon = convertTonToKilo(carbon);
        break;
      case TCarbonUnit.gCO2e:
        convertedCarbon = convertTonToGram(carbon);
        break;
      case TCarbonUnit.tCO2e:
        break;
      default:
        // Find the best suited unit and convert
        if (isInRange(carbon, CARBON_UNIT_RANGES[TCarbonUnit.kgCO2e])) {
          convertedCarbon = convertTonToKilo(carbon);
          unit = TCarbonUnit.kgCO2e;
        } else if (isInRange(carbon, CARBON_UNIT_RANGES[TCarbonUnit.gCO2e])) {
          convertedCarbon = convertTonToGram(carbon);
          unit = TCarbonUnit.gCO2e;
        }
    }
  }
  // Format
  let formattedCarbon;
  if (convertedCarbon !== 0 && convertedCarbon < 0.01) {
    if (options?.shouldUnescape) {
      formattedCarbon = t("formatter:smallNumberUnescaped", {
        ns: "formatter",
      }); // formattedCarbon = "&lt; 0.01"
    } else {
      formattedCarbon = t("formatter:smallNumber", { ns: "formatter" }); // formattedCarbon = "< 0.01"
    }
  } else {
    formattedCarbon = t("formatter:number", {
      ns: "formatter",
      value: convertedCarbon,
    });
  }
  return { formattedCarbon, unit, convertedCarbon };
}

export function comparisonCarbonRatio({ x, y }: { x: number; y: number }) {
  return (x - y) / y;
}
